import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Lock, Crown, UserPlus, Check, Clock, Plus, Trash2, PlayCircle, BadgeCheck, Users, Gift, Coins, UploadCloud, X, Loader2, Link2, Bitcoin, ChevronLeft, ChevronRight, Sparkles, Flag, Ban, CalendarClock, Gift as GiftIcon } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { uploadToStorage as uploadMedia, capturePoster } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import { TipGoalBar, GoalCelebration, useCelebration } from "@/components/TipGoal";
import { nextBestTime } from "@/components/AnalyticsExtras";
import { getTheme } from "@/lib/themes";

const FALLBACK = "https://images.unsplash.com/photo-1760512992083-8e3ab2671ef7";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CreatorProfile() {
  const { id } = useParams();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [subLoading, setSubLoading] = useState(false);
  const isSelf = user?.id === id;
  const [reportOpen, setReportOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState({ type: "user", id: null });
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const REPORT_REASONS = ["Underage / appears to be a minor", "Non-consensual content", "Harassment or threats", "Spam or scam", "Impersonation", "Other"];
  const openReport = (type, tid) => { setReportTarget({ type, id: tid }); setReportReason(""); setReportDetails(""); setReportOpen(true); };
  const submitReport = async () => {
    if (!reportReason) { toast.error("Please pick a reason"); return; }
    setReportBusy(true);
    try {
      await api.post("/report", { target_type: reportTarget.type, target_id: reportTarget.id, reason: reportReason, details: reportDetails });
      setReportOpen(false);
      toast.success("Report sent to our safety team 🛡️");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send report"); }
    finally { setReportBusy(false); }
  };
  const blockUser = async () => {
    if (!window.confirm(`Block ${profile?.name || "this member"}? They'll be hidden from you and any match removed.`)) return;
    try { await api.post("/block", { target_id: id }); toast.success("Blocked — you won't see each other anymore."); navigate("/discover"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't block"); }
  };
  const [form, setForm] = useState({ type: "photo", media_url: "", poster_url: "", caption: "", tier_level: 0 });
  const [scheduleAt, setScheduleAt] = useState("");
  const [showTip, setShowTip] = useState(false);
  const [showTiers, setShowTiers] = useState(false);
  const [customTip, setCustomTip] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [preview, setPreview] = useState(null); // { url, type }
  const [dragOver, setDragOver] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [lightbox, setLightbox] = useState(null); // photo index

  const [goal, setGoal] = useState(null);
  const [celebrate, fireCelebration] = useCelebration();
  const load = useCallback(() => {
    api.get(`/profile/${id}`).then((r) => setProfile(r.data)).catch(() => {});
    api.get("/tip-goals", { params: { ids: id } }).then((r) => setGoal(r.data[id] || null)).catch(() => {});
    api.get(`/posts/${id}`).then((r) => setPosts(r.data)).catch(() => {});
  }, [id]);
  useEffect(load, [load]);

  useEffect(() => {
    if (isSelf || !posts.length) return;
    posts.forEach((p) => { if (!p.locked) api.post(`/posts/${p.id}/view`).catch(() => {}); });
  }, [posts, isSelf]);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { toast.success("Payment confirmed 🎉 Thank you!"); setParams({}); load(); return; }
        if (data.status === "expired") { toast.error("Payment expired"); setParams({}); return; }
      } catch (e) {}
      if (attempts++ < 5) setTimeout(poll, 2000); else { setParams({}); }
    };
    toast.message("Confirming payment…");
    poll();
  }, [params, setParams, load]);

  useEffect(() => {
    const order = params.get("crypto_order");
    if (!order) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/coins/crypto/status/${order}`);
        if (data.payment_status === "paid") { toast.success("Crypto payment confirmed 🎉 Content unlocked!"); setParams({}); load(); return; }
      } catch (e) {}
      if (tries++ < 20) setTimeout(poll, 4000); else { toast.message("Your crypto payment is still confirming — access unlocks once it's done."); setParams({}); }
    };
    toast.message("Confirming crypto payment…");
    poll();
  }, [params, setParams, load]);

  const subscribe = async (tierLevel = 1) => {
    setSubLoading(true);
    try {
      const { data } = await api.post("/subscribe/checkout", { creator_id: id, origin_url: window.location.origin, tier_level: tierLevel });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setSubLoading(false); }
  };

  const subscribeCrypto = async (tierLevel = 1) => {
    setSubLoading(true);
    try {
      const { data } = await api.post("/subscribe/crypto/checkout", { creator_id: id, origin_url: window.location.origin, tier_level: tierLevel });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setSubLoading(false); }
  };

  const sendFriendRequest = async () => {
    const { data } = await api.post("/friends/request", { to_id: id });
    toast.success(data.status === "friends" ? "You're now friends" : "Friend request sent");
    load();
  };

  const sendTip = async (amount) => {
    if (!amount || amount < 1) return toast.error("Enter a valid amount");
    try {
      const { data } = await api.post("/tip/checkout", { creator_id: id, amount, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const tipCoins = async (amount) => {
    if (!amount || amount < 1) return;
    try {
      const { data } = await api.post("/tip/coins", { creator_id: id, amount });
      toast.success(`Tipped ${amount} Empire Coins 🪙`);
      if (data.goal) { setGoal(data.goal.goal); if (data.goal.reached) fireCelebration(profile?.name, data.goal.goal.title); }
      await refreshUser();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleVerify = async () => {
    try {
      await api.post("/admin/verify", { user_id: id, verified: !profile.verified });
      toast.success(!profile.verified ? "Creator verified ✓" : "Verification removed");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadToStorage = (blob, filename, contentType, mediaType, onPct) => uploadMedia(blob, filename, contentType, mediaType, onPct);

  const handleFile = async (file) => {
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");
    if (!isVideo && !isImage) return toast.error("Please choose an image or video file");
    if (file.size > 200 * 1024 * 1024) return toast.error("File is too large (max 200MB)");
    const mediaType = isVideo ? "video" : "photo";
    const localUrl = URL.createObjectURL(file);
    setPreview({ url: localUrl, type: mediaType });
    setForm((f) => ({ ...f, type: mediaType, media_url: "", poster_url: "" }));
    setUploading(true);
    setUploadPct(0);
    try {
      const path = await uploadToStorage(file, file.name, file.type, mediaType, setUploadPct);
      let posterUrl = "";
      if (isVideo) {
        const posterBlob = await capturePoster(file);
        if (posterBlob) {
          try {
            const pPath = await uploadToStorage(posterBlob, "poster.jpg", "image/jpeg", "photo", null);
            posterUrl = `${BACKEND}/api/media/file/${pPath}`;
          } catch (e) {}
        }
      }
      const url = `${BACKEND}/api/media/file/${path}`;
      setForm((f) => ({ ...f, type: mediaType, media_url: url, poster_url: posterUrl }));
      toast.success("Media uploaded — ready to publish ✨");
    } catch (err) {
      setPreview(null);
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed — please try again");
    } finally { setUploading(false); }
  };

  const clearMedia = () => { setPreview(null); setForm((f) => ({ ...f, media_url: "", poster_url: "" })); setUploadPct(0); };

  const createPost = async (e) => {
    e.preventDefault();
    if (!form.media_url) return toast.error("Upload a photo/video or paste a media URL");
    if (uploading) return toast.error("Please wait for the upload to finish");
    try {
      const publish_at = scheduleAt ? new Date(scheduleAt).toISOString() : null;
      await api.post("/posts", { ...form, publish_at });
      toast.success(publish_at ? `Scheduled for ${new Date(publish_at).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit", month: "short", day: "numeric" })}` : "Post published");
      setForm({ type: "photo", media_url: "", poster_url: "", caption: "", tier_level: 0 });
      setScheduleAt("");
      setPreview(null); setUploadPct(0); setShowUrlInput(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const useBestTime = async () => {
    try {
      const { data } = await api.get("/creator/analytics");
      const d = nextBestTime(data.post_activity || []);
      const pad = (n) => String(n).padStart(2, "0");
      setScheduleAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    } catch { toast.error("Couldn't load your best time"); }
  };

  const deletePost = async (pid) => { await api.delete(`/posts/${pid}`); load(); };

  if (!profile) return <div className="p-12 text-[#A0A0A0]">Loading…</div>;

  const T = getTheme(profile.theme);
  const skinBg = !profile.locked ? (profile.skin_bg || "") : "";
  const acc = (!profile.locked && profile.skin_accent) ? profile.skin_accent : null;
  const _hx = (h) => { const n = h.replace("#", ""); const b = n.length === 3 ? n.split("").map((x) => x + x).join("") : n; return [parseInt(b.slice(0, 2), 16), parseInt(b.slice(2, 4), 16), parseInt(b.slice(4, 6), 16)]; };
  const hexRgba = (h, a) => { try { const [r, g, b] = _hx(h); return `rgba(${r},${g},${b},${a})`; } catch { return h; } };
  const readableOn = (h) => { try { const [r, g, b] = _hx(h); return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? "#0A0A0A" : "#FFFFFF"; } catch { return "#0A0A0A"; } };
  const glowA = acc ? hexRgba(acc, 0.30) : T.glow;
  const glowB = acc ? hexRgba(acc, 0.18) : T.glow2;
  const ringC = acc || T.ring;
  const accentSolid = acc ? { background: acc, color: readableOn(acc) } : undefined;
  const accentChip = acc ? { background: hexRgba(acc, 0.15), color: acc, borderColor: hexRgba(acc, 0.4) } : undefined;
  const accentText = acc ? { color: acc } : undefined;
  const photos = (profile.photos || []).filter(Boolean);
  const coverImg = (!profile.locked && profile.cover_photo) || photos[0] || FALLBACK;
  const galleryPhotos = profile.locked ? [] : photos;
  const primaryBtn = `${T.accentPrimary} px-6 py-2.5 rounded-full text-sm shadow-lg transition-transform hover:scale-[1.03] active:scale-95 disabled:opacity-60`;
  const outlineBtn = `border ${T.accentSecondary} px-5 py-2.5 rounded-full text-sm transition-colors disabled:opacity-60 flex items-center gap-2`;
  const glass = `${T.cardSurface} border backdrop-blur-xl`;

  return (
    <div data-testid="profile-page" className="min-h-screen relative overflow-hidden" style={{ background: T.pageBg }}>
      {/* Creator custom skin — their own picture as the full-page backdrop */}
      {skinBg && (
        <div aria-hidden data-testid="custom-skin" className="fixed inset-0 z-0">
          <img src={skinBg} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${T.pageBg}CC, ${T.pageBg}F2 55%, ${T.pageBg})` }} />
        </div>
      )}
      {/* Ambient royalty glow orbs */}
      <motion.div aria-hidden className="pointer-events-none absolute -top-32 -right-24 w-[32rem] h-[32rem] rounded-full blur-3xl"
        style={{ background: `radial-gradient(circle, ${glowA} 0%, transparent 70%)` }}
        animate={{ x: [0, 30, 0], y: [0, 40, 0] }} transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div aria-hidden className="pointer-events-none absolute top-1/3 -left-32 w-[28rem] h-[28rem] rounded-full blur-3xl"
        style={{ background: `radial-gradient(circle, ${glowB} 0%, transparent 70%)` }}
        animate={{ x: [0, 40, 0], y: [0, -30, 0] }} transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div aria-hidden className="pointer-events-none absolute bottom-0 right-1/4 w-80 h-80 rounded-full blur-3xl"
        style={{ background: `radial-gradient(circle, ${glowA} 0%, transparent 70%)` }}
        animate={{ y: [0, -40, 0] }} transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }} />

      <div className="relative z-10 max-w-3xl mx-auto p-5 md:p-10">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className={`${T.textAccent} opacity-80 hover:opacity-100 flex items-center gap-2 mb-6 transition-opacity`}>
          <ArrowLeft size={20} /> Back
        </button>

        {/* HERO */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className={`${glass} rounded-[2rem] overflow-hidden mb-6`}>
          <div className="relative h-64 md:h-80">
            <img src={coverImg} alt={profile.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${T.pageBg} 4%, rgba(0,0,0,0.35) 45%, transparent 75%)` }} />
            {profile.is_creator && (
              <span style={accentSolid} className={`absolute top-4 right-4 text-xs px-3 py-1.5 rounded-full ${T.accentPrimary} flex items-center gap-1 shadow-lg`}>
                <Crown size={12} /> Creator
              </span>
            )}
            <div className="absolute bottom-5 left-5 md:left-7 right-5 flex items-end gap-4">
              {/* Avatar with pulsing royal halo */}
              <motion.div className="relative flex-shrink-0"
                animate={{ boxShadow: [`0 0 0px ${ringC}00`, `0 0 32px ${ringC}`, `0 0 0px ${ringC}00`] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                style={{ borderRadius: "1.25rem" }}>
                <img src={photos[0] || FALLBACK} alt="" className="w-20 h-20 md:w-24 md:h-24 rounded-[1.25rem] object-cover border-2" style={{ borderColor: ringC }} />
              </motion.div>
              <div className="min-w-0 pb-1">
                <div className={`font-heading text-3xl md:text-4xl ${T.textPrimary} flex items-center gap-2 leading-tight`} style={{ textShadow: "0 2px 20px rgba(0,0,0,0.6)" }}>
                  <span className="truncate">{profile.name}{profile.age ? `, ${profile.age}` : ""}</span>
                  {profile.verified && <BadgeCheck size={22} className={T.textAccent} data-testid="verified-badge" />}
                </div>
                <div className={`text-sm ${T.textPrimary} opacity-75`}>{profile.location || "Worldwide"} · {profile.gender}</div>
                {profile.is_creator && (
                  <div className={`text-xs ${T.textAccent} flex items-center gap-1 mt-1`} data-testid="sub-count">
                    <Users size={12} /> {profile.subscriber_count || 0} subscribers
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-6">
            {profile.locked ? (
              <div className={`flex items-center gap-2 ${T.textPrimary} opacity-80 text-sm`}><Lock size={16} /> This profile is private. Send a friend request to view.</div>
            ) : profile.bio ? (
              <p className={`text-sm ${T.textPrimary} opacity-90 leading-relaxed`}>{profile.bio}</p>
            ) : (
              <div data-testid="bio-empty" className={`flex items-start gap-2 text-sm ${T.textPrimary} opacity-80 italic`} style={accentText}>
                <Sparkles size={16} className="mt-0.5 flex-shrink-0" style={accentText} />
                <span>{isSelf
                  ? "Add a bio to draw them in — hit Edit Profile and tell them what you're into."
                  : `${profile.name} is fresh to the Empire ✨ Be the first to say hello.`}</span>
              </div>
            )}

            {!profile.locked && (profile.looking_for?.length > 0 || profile.interests?.length > 0 || profile.prompts?.length > 0) && (
              <div className="mt-5 space-y-4">
                {profile.looking_for?.length > 0 && (
                  <div>
                    <div className={`text-xs ${T.textPrimary} opacity-60 mb-2 uppercase tracking-widest`}>Looking for</div>
                    <div className="flex flex-wrap gap-2">
                      {profile.looking_for.map((v) => (
                        <span key={v} style={accentChip} className={`text-xs px-3 py-1.5 rounded-full border ${T.badge} font-medium`}>{v}</span>
                      ))}
                    </div>
                  </div>
                )}
                {profile.interests?.length > 0 && (
                  <div>
                    <div className={`text-xs ${T.textPrimary} opacity-60 mb-2 uppercase tracking-widest`}>Into</div>
                    <div className="flex flex-wrap gap-2">
                      {profile.interests.map((t, i) => (
                        <span key={i} style={accentChip} className={`text-xs px-3 py-1.5 rounded-full border ${T.accentSecondary}`}>{t}</span>
                      ))}
                    </div>
                  </div>
                )}
                {profile.prompts?.filter((p) => p.a?.trim()).map((p, i) => (
                  <motion.div key={i} data-testid={`profile-prompt-${i}`}
                    initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    className={`${glass} rounded-2xl p-4`}>
                    <div className={`text-xs ${T.textAccent} mb-1 flex items-center gap-1`} style={accentText}><Sparkles size={11} /> {p.q}</div>
                    <div className={`${T.textPrimary}`}>{p.a}</div>
                  </motion.div>
                ))}
              </div>
            )}

            {!isSelf && (
              <div className="flex flex-wrap gap-3 mt-6">
                {profile.friend_request_status === "friends" ? (
                  <span className={outlineBtn}><Check size={16} /> Friends</span>
                ) : profile.friend_request_status === "sent" ? (
                  <span className={`px-5 py-2.5 rounded-full text-sm ${T.textPrimary} opacity-60 border border-white/10 flex items-center gap-2`}><Clock size={16} /> Request sent</span>
                ) : (
                  <button data-testid="add-friend-btn" onClick={sendFriendRequest} className={outlineBtn}>
                    <UserPlus size={16} /> {profile.friend_request_status === "incoming" ? "Accept request" : "Add friend"}
                  </button>
                )}
                {profile.is_creator && (profile.subscribed ? (
                  <span className={`${primaryBtn} flex items-center gap-2`}><Check size={16} /> Subscribed</span>
                ) : (
                  <button data-testid="subscribe-btn" style={accentSolid} onClick={() => ((profile.tiers?.length || 0) > 1 ? setShowTiers((s) => !s) : subscribe(1))} disabled={subLoading} className={primaryBtn}>
                    {subLoading ? "Redirecting…" : (profile.tiers?.length || 0) > 1 ? "Choose a plan" : `Subscribe · $${Number(profile.subscription_price || 0).toFixed(2)}/mo`}
                  </button>
                ))}
                {profile.is_creator && !profile.subscribed && (profile.tiers?.length || 0) <= 1 && Number(profile.subscription_price || 0) >= 12 && (
                  <button data-testid="subscribe-crypto-btn" onClick={() => subscribeCrypto(1)} disabled={subLoading} className={outlineBtn}>
                    <Bitcoin size={16} /> Pay with crypto
                  </button>
                )}
                {profile.is_creator && (
                  <button data-testid="tip-btn" onClick={() => setShowTip((s) => !s)} className={outlineBtn}>
                    <Gift size={16} /> Send a tip
                  </button>
                )}
                {user?.role === "admin" && (
                  <button data-testid="verify-toggle" onClick={toggleVerify} className={outlineBtn}>
                    <BadgeCheck size={16} /> {profile.verified ? "Un-verify" : "Verify creator"}
                  </button>
                )}
                <button data-testid="report-btn" onClick={() => openReport("user", id)}
                  className={`${outlineBtn} !border-[#E63946]/40 !text-[#E63946]`}>
                  <Flag size={16} /> Report
                </button>
                <button data-testid="block-btn" onClick={blockUser}
                  className={`${outlineBtn} !border-[#E63946]/40 !text-[#E63946]`}>
                  <Ban size={16} /> Block
                </button>
              </div>
            )}

            {showTiers && !isSelf && !profile.subscribed && (profile.tiers?.length || 0) > 0 && (
              <div className="mt-5 grid sm:grid-cols-3 gap-3" data-testid="tier-panel">
                {profile.tiers.map((t, ti) => (
                  <motion.div key={t.level} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: ti * 0.06 }}
                    className={`${glass} rounded-2xl p-4 flex flex-col`}>
                    <div className={`font-heading text-xl ${T.textPrimary}`}>{t.name}</div>
                    <div className={`${T.textAccent} font-heading text-2xl my-1`} style={accentText}>${Number(t.price).toFixed(2)}<span className={`text-xs ${T.textPrimary} opacity-60`}>/mo</span></div>
                    <div className={`text-xs ${T.textPrimary} opacity-70 flex-1 mb-3`}>{t.description || "Access to this tier's content."}</div>
                    <button data-testid={`subscribe-tier-${t.level}`} style={accentSolid} onClick={() => subscribe(t.level)} disabled={subLoading} className={`${primaryBtn} w-full text-center`}>{subLoading ? "…" : "Subscribe"}</button>
                    {Number(t.price) >= 12 && (
                      <button data-testid={`subscribe-crypto-tier-${t.level}`} onClick={() => subscribeCrypto(t.level)} disabled={subLoading} className={`${outlineBtn} mt-2 justify-center text-xs py-1.5`}>
                        <Bitcoin size={13} /> Pay with crypto
                      </button>
                    )}
                  </motion.div>
                ))}
              </div>
            )}

            {goal && <div className="mt-5"><TipGoalBar goal={goal} testid="profile-tip-goal" /></div>}
            <GoalCelebration celebrate={celebrate} />

            {showTip && !isSelf && (
              <div className={`mt-5 ${glass} rounded-2xl p-4`} data-testid="tip-panel">
                <div className={`text-sm ${T.textPrimary} opacity-80 mb-3`}>Send {profile.name} a tip 💛</div>
                <div className="flex flex-wrap gap-2">
                  {[5, 10, 20, 50].map((amt) => (
                    <button key={amt} data-testid={`tip-${amt}`} style={accentSolid} onClick={() => sendTip(amt)} className={`${primaryBtn} !px-4 !py-2`}>${amt}</button>
                  ))}
                  <div className="flex items-center gap-2">
                    <input data-testid="tip-custom-input" type="number" min="1" max="500" placeholder="Custom" value={customTip} onChange={(e) => setCustomTip(e.target.value)} className="pe-input rounded-full px-4 py-2 w-28" />
                    <button data-testid="tip-custom-send" onClick={() => sendTip(Number(customTip))} className={outlineBtn}>Send</button>
                  </div>
                </div>
                <div className="border-t border-white/10 mt-4 pt-3">
                  <div className={`text-xs ${T.textPrimary} opacity-70 mb-2 flex items-center gap-1`}>
                    <Coins size={13} className={T.textAccent} /> Or tip instantly with Empire Coins · balance: <span className={T.textAccent}>{user?.coins || 0}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[10, 50, 100, 250].map((amt) => (
                      <button key={amt} data-testid={`coin-tip-${amt}`} onClick={() => tipCoins(amt)} disabled={(user?.coins || 0) < amt} className={`${outlineBtn} disabled:opacity-40`}>🪙 {amt}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* PHOTO GALLERY */}
        {galleryPhotos.length > 1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6" data-testid="photo-gallery">
            <div className={`text-xs ${T.textPrimary} opacity-60 mb-2 uppercase tracking-widest`}>Gallery</div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-none">
              {galleryPhotos.map((ph, i) => (
                <motion.button key={i} data-testid={`gallery-thumb-${i}`} onClick={() => setLightbox(i)}
                  whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
                  className={`flex-shrink-0 w-28 h-36 md:w-32 md:h-44 rounded-2xl overflow-hidden border ${T.cardSurface} border-white/10`}>
                  <img src={ph} alt="" className="w-full h-full object-cover" />
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Create post (own creator profile) */}
        {isSelf && user?.is_creator && (
          <form onSubmit={createPost} className={`${glass} rounded-2xl p-5 mb-6 space-y-4`}>
            <div className={`font-heading text-2xl ${T.textPrimary}`}>Publish new content</div>
            {preview ? (
              <div className="relative rounded-xl overflow-hidden border border-white/10" data-testid="post-preview">
                {preview.type === "video" ? (
                  <video src={preview.url} controls className="w-full max-h-80 object-contain bg-black" />
                ) : (
                  <img src={preview.url} alt="preview" className="w-full max-h-80 object-contain bg-black" />
                )}
                {uploading && (
                  <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-3">
                    <Loader2 size={28} className={`animate-spin ${T.textAccent}`} />
                    <div className="w-2/3 h-2 rounded-full bg-white/10 overflow-hidden">
                      <div className={`h-full ${T.accentPrimary} transition-all`} style={{ width: `${uploadPct}%` }} data-testid="upload-progress-bar" />
                    </div>
                    <span className={`text-xs ${T.textAccent}`} data-testid="upload-progress-text">Uploading… {uploadPct}%</span>
                  </div>
                )}
                {!uploading && (
                  <button type="button" data-testid="post-clear-media" onClick={clearMedia} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 border border-white/20 flex items-center justify-center text-white hover:bg-black">
                    <X size={16} />
                  </button>
                )}
              </div>
            ) : (
              <label data-testid="post-dropzone"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
                className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed cursor-pointer py-10 px-4 text-center transition-colors ${dragOver ? "border-white/60 bg-white/5" : "border-white/15 hover:border-white/40"}`}>
                <UploadCloud size={32} className={T.textAccent} />
                <div className={`text-sm ${T.textPrimary}`}>Drag & drop or <span className={T.textAccent}>browse</span></div>
                <div className={`text-xs ${T.textPrimary} opacity-60`}>Photos & videos · up to 200MB</div>
                <input data-testid="post-file-input" type="file" accept="image/*,video/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
              </label>
            )}
            <div>
              <button type="button" data-testid="post-url-toggle" onClick={() => setShowUrlInput((s) => !s)} className={`text-xs ${T.textPrimary} opacity-60 hover:opacity-100 flex items-center gap-1`}>
                <Link2 size={12} /> {showUrlInput ? "Hide URL option" : "Or paste a media URL instead"}
              </button>
              {showUrlInput && (
                <input data-testid="post-media" placeholder="https://…" value={form.media_url} onChange={(e) => { setForm({ ...form, media_url: e.target.value }); if (e.target.value) setPreview(null); }} className="pe-input w-full rounded-lg px-4 py-2 mt-2" />
              )}
            </div>
            <input data-testid="post-caption" placeholder="Caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="pe-input w-full rounded-lg px-4 py-2" />
            <div>
              <div className={`text-xs ${T.textPrimary} opacity-60 mb-1`}>Who can see this?</div>
              <select data-testid="post-tier" value={form.tier_level} onChange={(e) => setForm({ ...form, tier_level: Number(e.target.value) })} className="pe-input w-full rounded-lg px-4 py-2">
                <option value={0}>Free — everyone</option>
                {(user?.tiers && user.tiers.length ? user.tiers : [{ level: 1, name: "Premium", price: user?.subscription_price || 0 }]).map((t) => (
                  <option key={t.level} value={t.level}>{`${t.name} tier & up ($${Number(t.price).toFixed(2)}/mo)`}</option>
                ))}
              </select>
            </div>
            <div data-testid="post-schedule">
              <div className={`text-xs ${T.textPrimary} opacity-60 mb-1`}>When should it go live?</div>
              <div className="flex flex-wrap items-center gap-2">
                <input data-testid="post-schedule-input" type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
                  className="pe-input rounded-lg px-4 py-2 text-sm [color-scheme:dark]" />
                <button type="button" data-testid="post-best-time" onClick={useBestTime}
                  className="text-xs px-3 py-2 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors inline-flex items-center gap-1.5"><CalendarClock size={13} /> Use my best time</button>
                {scheduleAt && <button type="button" data-testid="post-schedule-clear" onClick={() => setScheduleAt("")} className={`text-xs ${T.textPrimary} opacity-60 hover:opacity-100`}>Post now instead</button>}
              </div>
            </div>
            <button data-testid="publish-post" type="submit" disabled={uploading || !form.media_url} className={`${primaryBtn} inline-flex items-center gap-2 disabled:opacity-50`}>{scheduleAt ? <><CalendarClock size={16} /> Schedule</> : <><Plus size={16} /> Publish</>}</button>
          </form>
        )}

        {/* Posts grid */}
        <div className="grid grid-cols-2 gap-3">
          {posts.map((p, pi) => (
            <motion.div key={p.id} data-testid={`post-${p.id}`}
              initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: (pi % 6) * 0.04 }}
              className={`relative rounded-2xl overflow-hidden border ${T.cardSurface} aspect-square group`}>
              {p.locked ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-center p-4" style={{ background: T.surface }}>
                  <Lock className={`${T.textAccent} mb-2`} size={28} />
                  <div className={`text-xs ${T.textPrimary} opacity-70`}>Subscribe to unlock</div>
                </div>
              ) : p.type === "video" ? (
                <div className="relative w-full h-full">
                  <video src={p.media_url} poster={p.poster_url || undefined} preload="metadata" className="w-full h-full object-cover" />
                  <PlayCircle className="absolute inset-0 m-auto text-white/90" size={40} />
                </div>
              ) : (
                <img src={p.media_url} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              )}
              {p.is_premium && <span className={`absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full ${T.accentPrimary}`}>PREMIUM</span>}
              {p.scheduled_for && (
                <span data-testid={`post-scheduled-${p.id}`} className="absolute top-9 left-2 right-10 text-[11px] px-2 py-1 rounded-lg bg-black/85 text-[#D4AF37] border border-[#D4AF37]/40 flex items-center gap-1">
                  <CalendarClock size={11} /> Goes live {new Date(p.scheduled_for).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </span>
              )}
              {p.gifted && <span data-testid={`post-gifted-${p.id}`} className="absolute top-8 left-2 text-[10px] px-2 py-0.5 rounded-full bg-black/80 text-[#D4AF37] border border-[#D4AF37]/50 inline-flex items-center gap-1"><GiftIcon size={10} /> GIFTED TO YOU</span>}
              {p.caption && !p.locked && <div className="absolute bottom-0 left-0 right-0 p-2 text-xs text-white bg-gradient-to-t from-black/80 to-transparent">{p.caption}</div>}
              {isSelf && <button onClick={() => deletePost(p.id)} className="absolute top-2 right-2 bg-black/70 text-[#E63946] p-1 rounded-full"><Trash2 size={14} /></button>}
              {!isSelf && !p.locked && <button data-testid={`report-post-${p.id}`} onClick={() => openReport("post", p.id)} title="Report this content" className="absolute top-2 right-2 bg-black/70 text-[#E63946] p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><Flag size={13} /></button>}
            </motion.div>
          ))}
        </div>
        {posts.length === 0 && (
          <motion.div data-testid="posts-empty" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className={`${glass} rounded-[2rem] px-6 py-10 md:py-12 text-center relative overflow-hidden`}>
            <div aria-hidden className="pointer-events-none absolute inset-0 opacity-60"
              style={{ background: `radial-gradient(circle at 50% 0%, ${glowA} 0%, transparent 60%)` }} />
            <div className="relative z-10 flex flex-col items-center">
              <motion.div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 border"
                style={{ borderColor: ringC, background: hexRgba(ringC, 0.12) }}
                animate={{ boxShadow: [`0 0 0px ${ringC}00`, `0 0 28px ${ringC}`, `0 0 0px ${ringC}00`] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}>
                <Crown size={30} style={{ color: ringC }} />
              </motion.div>
              <div className={`font-heading text-2xl md:text-3xl ${T.textPrimary}`}>
                {isSelf ? "Your stage is set 👑" : `${profile.name}'s Empire is just beginning`}
              </div>
              <p className={`text-sm ${T.textPrimary} opacity-70 max-w-sm mt-2`}>
                {isSelf
                  ? "Publish your first photo or video above to light up your profile and start drawing fans in."
                  : "Fresh content is on the way. Send a tip or a friend request to be first in line when it drops."}
              </p>
              <div aria-hidden className="grid grid-cols-3 gap-3 w-full max-w-xs mt-7">
                {[0, 1, 2].map((i) => (
                  <motion.div key={i} className={`aspect-square rounded-xl border ${T.cardSurface} border-white/10`}
                    style={{ background: hexRgba(ringC, 0.06) }}
                    animate={{ opacity: [0.3, 0.7, 0.3] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }} />
                ))}
              </div>
              {isSelf && user?.is_creator && (
                <div className={`text-xs ${T.textAccent} mt-5 flex items-center gap-1.5`} style={accentText}>
                  <Sparkles size={13} /> Tip: profiles with 3+ posts get far more subscribers.
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* Report modal */}
      <AnimatePresence>
        {reportOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="report-modal"
            onClick={() => setReportOpen(false)}>
            <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl border border-[#E63946]/30 bg-[#141414] p-6">
              <div className="flex items-center gap-2 mb-1"><Flag className="text-[#E63946]" size={20} /><h3 className="font-heading text-2xl text-white">Report {reportTarget.type === "post" ? "content" : "member"}</h3></div>
              <p className="text-xs text-[#A0A0A0] mb-4">Our safety team reviews every report. For emergencies or illegal content involving a minor, we act immediately.</p>
              <div className="space-y-2 mb-4">
                {REPORT_REASONS.map((r) => (
                  <button key={r} data-testid={`report-reason-${r}`} onClick={() => setReportReason(r)}
                    className={`w-full text-left text-sm px-4 py-2.5 rounded-lg border transition-colors ${reportReason === r ? "border-[#E63946] bg-[#E63946]/10 text-white" : "border-[#2A2A2A] text-[#A0A0A0] hover:text-white"}`}>
                    {r}
                  </button>
                ))}
              </div>
              <textarea data-testid="report-details" value={reportDetails} onChange={(e) => setReportDetails(e.target.value)}
                rows={3} placeholder="Add any details (optional)" className="pe-input w-full rounded-lg px-3 py-2 text-sm mb-4" />
              <div className="flex gap-3 justify-end">
                <button data-testid="report-cancel" onClick={() => setReportOpen(false)} className="px-5 py-2 rounded-full text-sm text-[#A0A0A0] hover:text-white">Cancel</button>
                <button data-testid="report-submit" onClick={submitReport} disabled={reportBusy}
                  className="px-6 py-2 rounded-full text-sm bg-[#E63946] text-white font-medium disabled:opacity-60 flex items-center gap-2">
                  {reportBusy ? <Loader2 size={14} className="animate-spin" /> : <Flag size={14} />} Submit report
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && galleryPhotos[lightbox] && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/92 backdrop-blur-sm flex items-center justify-center p-4" data-testid="photo-lightbox"
            onClick={() => setLightbox(null)}>
            <button data-testid="lightbox-close" className="absolute top-5 right-5 text-white/80 hover:text-white" onClick={() => setLightbox(null)}><X size={28} /></button>
            {galleryPhotos.length > 1 && (
              <button data-testid="lightbox-prev" className="absolute left-3 md:left-8 text-white/70 hover:text-white p-2"
                onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + galleryPhotos.length) % galleryPhotos.length); }}>
                <ChevronLeft size={40} />
              </button>
            )}
            <motion.img key={lightbox} initial={{ scale: 0.94, opacity: 0.6 }} animate={{ scale: 1, opacity: 1 }}
              src={galleryPhotos[lightbox]} alt="" className="max-h-[85vh] max-w-full rounded-2xl object-contain"
              style={{ boxShadow: `0 0 60px ${glowA}` }} onClick={(e) => e.stopPropagation()} />
            {galleryPhotos.length > 1 && (
              <button data-testid="lightbox-next" className="absolute right-3 md:right-8 text-white/70 hover:text-white p-2"
                onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % galleryPhotos.length); }}>
                <ChevronRight size={40} />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
